"use client";

import React, { Suspense, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import OnBoarding from "@/app/onboarding/_components/OnBoarding";
import { useAppData } from "@/store/appStore";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { getCookieConsent, setCookieConsent } from "@/lib/utils/cookie-consent";

const stepQueryParamMapping: { [key: number]: string } = {
	0: "email",
	1: "email-otp",
	2: "mobile",
	3: "mobile-otp",
	4: "salary",
	5: "user-info",
	6: "device-selection",
};

const stepSlugs = Object.values(stepQueryParamMapping);

const ClientOnboarding = () => {
	const router = useRouter();
	const [, setAppState] = useAppData();

	const FIRST_STEP_INDEX = 0;
	const LAST_STEP_INDEX = stepSlugs.length - 1;

	const [stepSlug, setStepSlug] = useQueryState(
		"step",
		parseAsStringEnum(stepSlugs)
			.withDefault(stepSlugs[FIRST_STEP_INDEX])
			.withOptions({ history: "replace" }),
	);

	const derivedIndex = stepSlugs.indexOf(
		stepSlug ?? stepSlugs[FIRST_STEP_INDEX],
	);
	const currentMainStep = derivedIndex === -1 ? FIRST_STEP_INDEX : derivedIndex;

	// Prevent browser back button from navigating away during onboarding
	const stepSlugRef = useRef(stepSlug);

	useEffect(() => {
		const handlePopState = () => {
			// Ref still holds the correct step (React hasn't re-rendered yet)
			const correctStep = stepSlugRef.current || stepSlugs[FIRST_STEP_INDEX];
			const url = new URL(window.location.href);
			url.searchParams.set("step", correctStep);
			history.pushState(null, "", url.toString());
		};

		window.addEventListener("popstate", handlePopState);
		return () => window.removeEventListener("popstate", handlePopState);
	}, []);

	// Track current step in ref + push a buffer entry after each step change
	// (replace mode overwrites the previous buffer, so we re-push after every transition)
	useEffect(() => {
		stepSlugRef.current = stepSlug;
		// Skip pushState if URL step doesn't match (nuqs hasn't settled after hydration yet)
		const urlStep = new URL(window.location.href).searchParams.get("step");
		if (urlStep && urlStep !== stepSlug) return;
		history.pushState(null, "", window.location.href);
	}, [stepSlug]);

	// Reset to first step on page load/refresh only if no valid step is in URL
	useEffect(() => {
		// If we already have a valid step from URL params, don't override it
		if (derivedIndex !== -1) {
			return;
		}
		setStepSlug(stepSlugs[FIRST_STEP_INDEX], { scroll: false });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleOnboardingComplete = (completionType?: "cars" | "devices") => {
		// Auto-decline cookies if user never interacted with the consent popup
		if (getCookieConsent() === null) {
			setCookieConsent("declined");
		}
		setAppState({
			onboardingCompleted: true,
			onboardingCompletionType: completionType,
		});
		// Block back navigation to onboarding after completion
		// Persists after component unmount — only blocks /onboarding URLs, allows all other back navigation
		const blockOnboardingBack = () => {
			if (
				window.location.pathname.startsWith("/onboarding") &&
				document.cookie.includes("onboardingCompleted")
			) {
				history.pushState(null, "", "/");
			}
		};
		window.addEventListener("popstate", blockOnboardingBack);

		// Yield one tick so the browser commits the cookie before the navigation request
		setTimeout(() => router.push("/", { scroll: false }), 0);
	};

	const handleNextStep = (stepName?: string) => {
		if (stepName) {
			if (stepSlugs.includes(stepName)) {
				setStepSlug(stepName, { scroll: false });
				return;
			}
			console.warn(
				`[OnboardingClient] Unable to locate step slug "${stepName}", falling back to sequential navigation.`,
			);
		}

		const nextIndex =
			currentMainStep < LAST_STEP_INDEX ? currentMainStep + 1 : currentMainStep;
		const nextSlug = stepSlugs[nextIndex];
		if (nextSlug) {
			setStepSlug(nextSlug, { scroll: false });
		}
	};
	// If onboarding is already completed, show blank screen while middleware redirects to homepage
	// This prevents a flash of the last onboarding step when pressing back from homepage
	if (document.cookie.includes("onboardingCompleted")) {
		return <div className="fixed inset-0 z-[200] bg-white" />;
	}

	return (
		<Suspense
			fallback={
				<div className="fixed inset-0 z-[200] bg-white flex items-center justify-center"></div>
			}
		>
			<OnBoarding
				onComplete={handleOnboardingComplete}
				currentStep={currentMainStep}
				onNext={handleNextStep}
			/>
		</Suspense>
	);
};

export default ClientOnboarding;
